/**
 * Technician interface layout (v2 widget app).
 *
 * Fetches the full published interface for the current technician from
 * GET /fsa/technician/interface/ (all screens + widgets + theme) and caches
 * the last good copy in localStorage, so the v2 app keeps its layout when
 * offline — mirroring the useTechPolicy pattern.
 *
 * The payload already contains every screen (dashboard, jobs, job detail,
 * map, ...) with nested widgets, nav flags (route_path/show_in_nav/icon) and
 * per-widget config/style_overrides. This composable just loads + caches it
 * and exposes convenient lookups by route.
 */
import { ref, computed } from 'vue'
import axios from '@/api/client'
import { previewInterfaceVersion } from '@/preview/techPreview'

const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000 // survive long offline stretches

/** Current technician id (for per-user cache keys). '' when unknown. */
function _userId() {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const u = JSON.parse(raw)
    return String(u?.id || '')
  } catch {
    return ''
  }
}

// Per-technician cache key: never show another technician's cached layout in
// a shared browser or the admin emulator.
function _cacheKey() {
  return `fsa_interface_layout:${_userId()}`
}

/**
 * Built-in "System Default v2" layout.
 *
 * Baseline that reproduces the v1 app 1:1 by rendering each proven v1 screen
 * as a single native whole-screen widget. Used whenever a domain has NOT built
 * a custom interface yet, so v2 is immediately testable (and visually
 * identical) without seeding the database. As soon as the backend returns a
 * custom published layout, that DB layout wins and this default is ignored.
 */
const DEFAULT_V2_LAYOUT = {
  is_default_builtin: true,
  theme_config: {},
  screens: [
    {
      id: 'def-home', code: 'dashboard', name: 'Home', route_path: '/technician',
      is_home: true, show_in_nav: true, icon: 'mdi-view-dashboard', display_order: 1, layout_type: 'scroll',
      // Home is decomposed into reorderable per-section widgets (v1 parity),
      // matching the on-screen order of the hardcoded dashboard.
      widgets: [
        { id: 'w-home-status', widget_type_code: 'dash_status', display_order: 1, is_visible: true, width: 100 },
        { id: 'w-home-progress', widget_type_code: 'dash_progress', display_order: 2, is_visible: true, width: 100 },
        { id: 'w-home-events', widget_type_code: 'dash_events', display_order: 3, is_visible: true, width: 100 },
        { id: 'w-home-nextjob', widget_type_code: 'dash_next_job', display_order: 4, is_visible: true, width: 100 },
        { id: 'w-home-reviews', widget_type_code: 'dash_reviews', display_order: 5, is_visible: true, width: 100 },
        { id: 'w-home-tasks', widget_type_code: 'dash_tasks', display_order: 6, is_visible: true, width: 100 },
        { id: 'w-home-schedule', widget_type_code: 'dash_schedule', display_order: 7, is_visible: true, width: 100 },
        { id: 'w-home-time', widget_type_code: 'dash_time', display_order: 8, is_visible: true, width: 100 },
      ],
    },
    {
      id: 'def-jobs', code: 'jobs', name: 'Jobs', route_path: '/technician/jobs',
      show_in_nav: true, icon: 'mdi-clipboard-list', display_order: 2, layout_type: 'scroll',
      widgets: [{ id: 'w-jobs', widget_type_code: 'native_jobs', display_order: 1, is_visible: true, width: 100 }],
    },
    {
      id: 'def-jobdetail', code: 'job_detail', name: 'Job', route_path: '/technician/jobs/',
      show_in_nav: false, display_order: 3, layout_type: 'scroll',
      widgets: [{ id: 'w-jobdetail', widget_type_code: 'native_job_detail', display_order: 1, is_visible: true, width: 100 }],
    },
    {
      id: 'def-map', code: 'map', name: 'Map', route_path: '/technician/map',
      show_in_nav: true, icon: 'mdi-map-marker', display_order: 5, layout_type: 'fixed',
      widgets: [{ id: 'w-map', widget_type_code: 'native_map', display_order: 1, is_visible: true, width: 100 }],
    },
    {
      id: 'def-tasks', code: 'tasks', name: 'Tasks', route_path: '/technician/tasks',
      show_in_nav: true, icon: 'mdi-clipboard-check-outline', display_order: 3, layout_type: 'scroll',
      widgets: [{ id: 'w-tasks', widget_type_code: 'native_tasks', display_order: 1, is_visible: true, width: 100 }],
    },
    {
      id: 'def-truck', code: 'truck_stock', name: 'Truck', route_path: '/technician/truck-stock',
      show_in_nav: true, icon: 'mdi-truck-check-outline', display_order: 4, layout_type: 'scroll',
      widgets: [{ id: 'w-truck', widget_type_code: 'native_truck_stock', display_order: 1, is_visible: true, width: 100 }],
    },
    {
      id: 'def-profile', code: 'profile', name: 'Profile', route_path: '/technician/profile',
      show_in_nav: false, icon: 'mdi-account', display_order: 7, layout_type: 'scroll',
      // Profile decomposed into reorderable per-section widgets (v1 parity).
      widgets: [
        { id: 'w-prof-header', widget_type_code: 'profile_header', display_order: 1, is_visible: true, width: 100 },
        { id: 'w-prof-stats', widget_type_code: 'profile_stats', display_order: 2, is_visible: true, width: 100 },
        { id: 'w-prof-contact', widget_type_code: 'profile_contact', display_order: 3, is_visible: true, width: 100 },
        { id: 'w-prof-settings', widget_type_code: 'profile_settings', display_order: 4, is_visible: true, width: 100 },
        { id: 'w-prof-actions', widget_type_code: 'profile_actions', display_order: 5, is_visible: true, width: 100 },
      ],
    },
    {
      id: 'def-chat', code: 'chat', name: 'Chat', route_path: '/technician/chat',
      show_in_nav: false, icon: 'mdi-message-text', display_order: 8, layout_type: 'scroll',
      widgets: [{ id: 'w-chat', widget_type_code: 'native_chat', display_order: 1, is_visible: true, width: 100 }],
    },
    {
      id: 'def-notif', code: 'notifications', name: 'Alerts', route_path: '/technician/notifications',
      show_in_nav: false, icon: 'mdi-bell', display_order: 9, layout_type: 'scroll',
      widgets: [{ id: 'w-notif', widget_type_code: 'native_notifications', display_order: 1, is_visible: true, width: 100 }],
    },
  ],
}

function _readCache() {
  try {
    const raw = localStorage.getItem(_cacheKey())
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed._ts && Date.now() - parsed._ts > CACHE_TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

function _writeCache(data, revision) {
  try {
    localStorage.setItem(_cacheKey(), JSON.stringify({ data, revision, _ts: Date.now() }))
  } catch {
    /* storage full — non-fatal */
  }
}

// Cache-first: seed the layout from this technician's last good copy so the
// correct interface mounts instantly (no default/classic flash). revision is
// tracked so we only re-fetch + swap when the layout actually changed.
const _seed = _readCache()
const layout = ref(_seed?.data || null)
const revision = ref(_seed?.revision ?? null)
const loaded = ref(false)
let inflight = null

/**
 * Load the technician interface.
 *
 * @param {boolean} force        bypass the "already loaded" guard.
 * @param {number|null} knownRevision  published revision from the cheap meta
 *   check. When it equals the cached revision we already hold the right layout
 *   and skip the full fetch entirely (no request, no re-render, no flash).
 */
async function loadLayout(force = false, knownRevision = null) {
  const overrideId = previewInterfaceVersion()
  // Fast path: cached layout is already current (revision matches) — nothing
  // changed since last launch, so there is nothing to fetch or re-render.
  if (!force && !overrideId && layout.value
      && knownRevision != null && revision.value != null
      && Number(knownRevision) === Number(revision.value)) {
    loaded.value = true
    console.log('[InterfaceLayout] cache current (rev', revision.value, ') — no fetch')
    return layout.value
  }
  if (loaded.value && !force) return layout.value
  if (inflight) return inflight
  inflight = (async () => {
    try {
      // Builder Live Preview: force a specific version (draft included).
      const params = overrideId ? { version_id: overrideId } : undefined
      const { data } = await axios.get('/fsa/technician/interface/', { params })
      if (data && Array.isArray(data.screens) && data.screens.length) {
        const newRev = data.published_revision ?? null
        // Only swap the live object when the layout actually changed — avoids a
        // needless re-render (flash) when we already showed the cached copy.
        const changed = !layout.value
          || layout.value.id !== data.id
          || Number(newRev) !== Number(revision.value)
        if (changed) {
          layout.value = data
          revision.value = newRev
          console.log('[InterfaceLayout] loaded', data.screens.length, 'screens',
            overrideId ? `(LIVE PREVIEW override v${overrideId})` : `(custom, rev ${newRev})`)
        } else {
          console.log('[InterfaceLayout] fetched — unchanged (rev', newRev, '), kept cached view')
        }
        if (!overrideId) _writeCache(data, newRev) // never cache a preview override
        loaded.value = true
      } else {
        // Empty response. NEVER clobber a good cached custom layout with the
        // built-in default — a transient empty/partial answer must not make the
        // technician's HCP interface "flip" to the generic System Default and
        // then flip back ("плывёт"). Only fall back to the built-in when we have
        // nothing cached at all.
        if (!layout.value) {
          layout.value = DEFAULT_V2_LAYOUT
          revision.value = null
          console.log('[InterfaceLayout] no custom layout + no cache -> built-in System Default v2')
        } else {
          console.log('[InterfaceLayout] empty response — kept cached layout (no flip)')
        }
        loaded.value = true
      }
    } catch (e) {
      console.warn('[InterfaceLayout] load failed — keeping cached layout:', e?.message)
      // Same rule on error: keep whatever we already show; only seed the
      // built-in when there is genuinely nothing to render.
      if (!layout.value) layout.value = DEFAULT_V2_LAYOUT
    } finally {
      inflight = null
    }
    return layout.value
  })()
  return inflight
}

/** Clear THIS technician's cached layout (e.g. on logout / switch-view). */
export function clearLayoutCache(uid) {
  try {
    localStorage.removeItem(uid ? `fsa_interface_layout:${uid}` : _cacheKey())
  } catch { /* non-fatal */ }
}

export function useInterfaceLayout() {
  // NOTE: no auto-fetch here. The shell (TechnicianApp) triggers loadLayout()
  // via its v2 watcher and passes the known revision, so v1 technicians never
  // fire an interface request and v2 refetches only when the revision changed.

  const screens = computed(() => {
    const list = (layout.value?.screens || []).filter(s => s.is_visible !== false)
    return [...list].sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
  })

  const themeConfig = computed(() => layout.value?.theme_config || {})

  const navScreens = computed(() => screens.value.filter(s => s.show_in_nav !== false))

  const homeScreen = computed(() =>
    screens.value.find(s => s.is_home) || screens.value[0] || null
  )

  /**
   * Find the screen that matches a router path.
   *   1. exact route_path match;
   *   2. parametrised match: a route_path with :params (e.g.
   *      /technician/jobs/:id/checkout) is turned into a regex, so nested
   *      screens with a variable id in the MIDDLE of the path resolve
   *      correctly (prefix matching cannot, since the id varies before the
   *      suffix). The most specific (most static characters) pattern wins;
   *   3. longest route_path that prefixes the current path (legacy behavior);
   *   4. home screen.
   */
  function screenForRoute(path) {
    if (!path) return homeScreen.value
    const exact = screens.value.find(s => s.route_path && s.route_path === path)
    if (exact) return exact

    // Parametrised (:param) route_paths -> regex, pick the most specific.
    const paramMatches = []
    for (const s of screens.value) {
      const rp = s.route_path
      if (!rp || !rp.includes(':')) continue
      const staticLen = rp.replace(/:[^/]+/g, '').length
      const pattern = '^' + rp
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')   // escape regex meta
        .replace(/:[^/]+/g, '[^/]+')               // :param -> one segment
        + '/?$'
      try {
        if (new RegExp(pattern).test(path)) paramMatches.push({ s, staticLen })
      } catch { /* bad pattern — skip */ }
    }
    if (paramMatches.length) {
      paramMatches.sort((a, b) => b.staticLen - a.staticLen)
      return paramMatches[0].s
    }

    // Fallback: longest route_path that prefixes the current path.
    const byPrefix = screens.value
      .filter(s => s.route_path && path.startsWith(s.route_path))
      .sort((a, b) => (b.route_path.length - a.route_path.length))[0]
    return byPrefix || homeScreen.value
  }

  function visibleSortedWidgets(screen) {
    return (screen?.widgets || [])
      .filter(w => w.is_visible !== false)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
  }

  return {
    layout,
    loaded,
    loadLayout,
    screens,
    navScreens,
    homeScreen,
    themeConfig,
    screenForRoute,
    visibleSortedWidgets,
  }
}
